import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/lib/SiteContext";
import { Field, SaveBtn, UploadButton, inputCls } from "./kit";
import { assetUrl } from "@/lib/api";

export function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [checklist, setChecklist] = useState([]);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/checklist").then((r) => setChecklist(r.data)).catch(() => {});
  }, []);
  const cards = [
    { label: "Amazon clicks (interest)", value: stats?.purchase_click ?? "—", tid: "stat-purchase-clicks" },
    { label: "Trailer plays", value: stats?.trailer_play ?? "—", tid: "stat-trailer-plays" },
    { label: "Reader signups", value: stats?.subscribers ?? "—", tid: "stat-subscribers" },
    { label: "Inquiries", value: stats?.inquiries ?? "—", tid: "stat-inquiries" },
    { label: "Unread inquiries", value: stats?.unread_inquiries ?? "—", tid: "stat-unread" },
    { label: "Quote downloads", value: stats?.quote_download ?? "—", tid: "stat-quote-downloads" },
  ];
  return (
    <div data-testid="admin-tab-overview">
      <h2 className="font-display text-2xl text-paper mb-6">Overview</h2>
      <p className="font-ui text-xs text-paper/40 mb-6">
        Amazon clicks measure outbound interest only — they are not confirmed sales.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
        {cards.map((c) => (
          <div key={c.label} className="border border-paper/10 bg-coal p-6" data-testid={c.tid}>
            <p className="font-display text-3xl text-paper">{c.value}</p>
            <p className="font-ui text-[11px] uppercase tracking-[0.2em] text-paper/45 mt-2">{c.label}</p>
          </div>
        ))}
      </div>
      <h3 className="font-display text-xl text-paper mb-4">Studios analytics (real, anonymous)</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="video-stats">
        {[
          { label: "Video views", value: stats?.video_views ?? "—" },
          { label: "Watch minutes", value: stats?.video_watch_minutes ?? "—" },
          { label: "Completion rate", value: stats ? `${stats.video_completion_rate}%` : "—" },
        ].map((c) => (
          <div key={c.label} className="border border-paper/10 bg-coal p-6">
            <p className="font-display text-3xl text-paper">{c.value}</p>
            <p className="font-ui text-[11px] uppercase tracking-[0.2em] text-paper/45 mt-2">{c.label}</p>
          </div>
        ))}
      </div>
      {(stats?.top_videos || []).length > 0 && (
        <div className="mb-12">
          <p className="font-ui text-[11px] uppercase tracking-[0.2em] text-paper/45 mb-3">Most watched</p>
          <div className="divide-y divide-paper/10 border-y border-paper/10">
            {stats.top_videos.map((v) => (
              <div key={v.slug} className="py-3 flex justify-between gap-4">
                <span className="font-ui text-sm text-paper truncate">{v.title}</span>
                <span className="font-ui text-xs text-paper/45 shrink-0">{v.views || 0} views · {Math.round((v.watch_seconds || 0) / 60)} min</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <h3 className="font-display text-xl text-paper mb-4">Setup checklist</h3>
      {checklist.length === 0 ? (
        <p className="font-ui text-sm text-gold" data-testid="checklist-complete">Everything is configured. Well done.</p>
      ) : (
        <ul className="space-y-3" data-testid="setup-checklist">
          {checklist.map((c, i) => (
            <li key={i} className="font-ui text-sm text-paper/60 flex gap-3">
              <span className="text-crimsonhi">✦</span> {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SiteTab() {
  const { refresh } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return <p className="font-ui text-sm text-paper/40">Loading…</p>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setSocial = (k) => (e) => setS({ ...s, socials: { ...(s.socials || {}), [k]: e.target.value } });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Site copy saved");
      refresh();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="admin-tab-site">
      <h2 className="font-display text-2xl text-paper mb-8">Site Copy & Imagery</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
        <div>
          <Field label="Hero kicker"><input className={inputCls} value={s.hero_kicker || ""} onChange={set("hero_kicker")} data-testid="set-hero-kicker" /></Field>
          <Field label="Hero headline"><input className={inputCls} value={s.hero_headline || ""} onChange={set("hero_headline")} data-testid="set-hero-headline" /></Field>
          <Field label="Hero supporting line"><textarea rows={3} className={`${inputCls} resize-y`} value={s.hero_sub || ""} onChange={set("hero_sub")} data-testid="set-hero-sub" /></Field>
          <Field label="Editorial title"><input className={inputCls} value={s.editorial_title || ""} onChange={set("editorial_title")} data-testid="set-editorial-title" /></Field>
          <Field label="Editorial statement" hint="Original campaign copy — never presented as a quotation from the book.">
            <textarea rows={5} className={`${inputCls} resize-y`} value={s.editorial_body || ""} onChange={set("editorial_body")} data-testid="set-editorial-body" />
          </Field>
          <Field label="Marquee themes (comma separated)">
            <input className={inputCls} value={(s.marquee_items || []).join(", ")}
              onChange={(e) => setS({ ...s, marquee_items: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
              data-testid="set-marquee" />
          </Field>
          <Field label="Signup heading"><input className={inputCls} value={s.signup_heading || ""} onChange={set("signup_heading")} data-testid="set-signup-heading" /></Field>
          <Field label="Signup body"><textarea rows={2} className={`${inputCls} resize-y`} value={s.signup_body || ""} onChange={set("signup_body")} data-testid="set-signup-body" /></Field>
        </div>
        <div>
          <Field label="Author display name"><input className={inputCls} value={s.author_name || ""} onChange={set("author_name")} data-testid="set-author-name" /></Field>
          <Field label="Author full name"><input className={inputCls} value={s.author_full_name || ""} onChange={set("author_full_name")} data-testid="set-author-full" /></Field>
          <Field label="Author role line"><input className={inputCls} value={s.author_role || ""} onChange={set("author_role")} data-testid="set-author-role" /></Field>
          <Field label="Author biography"><textarea rows={4} className={`${inputCls} resize-y`} value={s.author_bio || ""} onChange={set("author_bio")} data-testid="set-author-bio" /></Field>
          <Field label="Book cover (front)" hint="Upload the official front cover art. Shown in the 3D book everywhere.">
            <div className="flex items-center gap-4">
              <UploadButton accept="image/*" onUploaded={(url) => setS({ ...s, cover_url: url })} label={s.cover_url ? "Replace cover" : "Upload cover"} tid="upload-cover" />
              {s.cover_url && <img src={assetUrl(s.cover_url)} alt="cover preview" className="h-20 w-auto border border-paper/15" data-testid="cover-preview" />}
            </div>
          </Field>
          <Field label="Spine artwork (optional)" hint="Used on the 3D book's spine face.">
            <div className="flex items-center gap-4">
              <UploadButton accept="image/*" onUploaded={(url) => setS({ ...s, spine_url: url })} label={s.spine_url ? "Replace spine" : "Upload spine"} tid="upload-spine" />
              {s.spine_url && <img src={assetUrl(s.spine_url)} alt="spine preview" className="h-20 w-auto border border-paper/15" data-testid="spine-preview" />}
            </div>
          </Field>
          <Field label="Back cover artwork (optional)" hint="Used on the 3D book's back face.">
            <div className="flex items-center gap-4">
              <UploadButton accept="image/*" onUploaded={(url) => setS({ ...s, back_url: url })} label={s.back_url ? "Replace back" : "Upload back"} tid="upload-back" />
              {s.back_url && <img src={assetUrl(s.back_url)} alt="back cover preview" className="h-20 w-auto border border-paper/15" data-testid="back-preview" />}
            </div>
          </Field>
          <Field label="Author portrait">
            <div className="flex items-center gap-4">
              <UploadButton accept="image/*" onUploaded={(url) => setS({ ...s, author_portrait_url: url })} label={s.author_portrait_url ? "Replace portrait" : "Upload portrait"} tid="upload-portrait" />
              {s.author_portrait_url && <img src={assetUrl(s.author_portrait_url)} alt="portrait preview" className="h-20 w-auto border border-paper/15" data-testid="portrait-preview" />}
            </div>
          </Field>
          <Field label="Public contact email"><input type="email" className={inputCls} value={s.contact_email || ""} onChange={set("contact_email")} data-testid="set-contact-email" /></Field>
          <Field label="Print release target (draft — never shown publicly)">
            <input type="date" className={inputCls} value={s.print_release_target || ""} onChange={set("print_release_target")} data-testid="set-print-target" />
          </Field>
        </div>
      </div>
      <h3 className="font-display text-xl text-paper mt-4 mb-4">Social links (empty = hidden)</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
        {["instagram", "facebook", "youtube", "x"].map((k) => (
          <Field key={k} label={k === "x" ? "X (Twitter)" : k.charAt(0).toUpperCase() + k.slice(1)}>
            <input className={inputCls} value={(s.socials || {})[k] || ""} onChange={setSocial(k)} placeholder="https://…" data-testid={`set-social-${k}`} />
          </Field>
        ))}
      </div>
      <h3 className="font-display text-xl text-paper mt-4 mb-4">SEO defaults</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
        <Field label="Site title"><input className={inputCls} value={s.seo_title || ""} onChange={set("seo_title")} data-testid="set-seo-title" /></Field>
        <Field label="Site description"><textarea rows={2} className={`${inputCls} resize-y`} value={s.seo_description || ""} onChange={set("seo_description")} data-testid="set-seo-desc" /></Field>
      </div>
      <div className="mt-6">
        <SaveBtn onClick={save} saving={saving} tid="site-save-btn" />
      </div>
    </div>
  );
}

const FORMAT_STATUSES = [
  { value: "available", label: "Available (public purchase button)" },
  { value: "coming", label: "In preparation (label only, no link)" },
  { value: "hidden", label: "Hidden (not shown anywhere)" },
];

export function BookTab() {
  const { refresh } = useSite();
  const [v, setV] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/volume").then((r) => setV(r.data)).catch(() => {});
  }, []);
  if (!v) return <p className="font-ui text-sm text-paper/40">Loading…</p>;
  const setFmt = (i, k, val) => {
    const formats = v.formats.map((f, fi) => (fi === i ? { ...f, [k]: val } : f));
    setV({ ...v, formats });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/volume", v);
      toast.success("Book details saved");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="admin-tab-book">
      <h2 className="font-display text-2xl text-paper mb-8">Book & Formats</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
        <Field label="Volume title"><input className={inputCls} value={v.title || ""} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="vol-title" /></Field>
        <Field label="Chronicle count"><input type="number" className={inputCls} value={v.chronicle_count || 0} onChange={(e) => setV({ ...v, chronicle_count: Number(e.target.value) })} data-testid="vol-count" /></Field>
      </div>
      <Field label="Synopsis"><textarea rows={4} className={`${inputCls} resize-y`} value={v.synopsis || ""} onChange={(e) => setV({ ...v, synopsis: e.target.value })} data-testid="vol-synopsis" /></Field>
      <h3 className="font-display text-xl text-paper mt-6 mb-4">Formats</h3>
      <div className="space-y-4" data-testid="formats-editor">
        {(v.formats || []).map((f, i) => (
          <div key={f.key} className="border border-paper/10 bg-coal p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-2">
              <p className="font-ui text-sm text-paper font-semibold">{f.label}</p>
            </div>
            <div className="md:col-span-3">
              <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-paper/45 mb-1.5">Status</label>
              <select className={inputCls} value={f.status} onChange={(e) => setFmt(i, "status", e.target.value)} data-testid={`format-status-${f.key}`}>
                {FORMAT_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-5">
              <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-paper/45 mb-1.5">Purchase URL</label>
              <input className={inputCls} value={f.url || ""} onChange={(e) => setFmt(i, "url", e.target.value)} placeholder="https://www.amazon.com/dp/…" data-testid={`format-url-${f.key}`} />
            </div>
            <div className="md:col-span-2">
              <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-paper/45 mb-1.5">Price (optional)</label>
              <input className={inputCls} value={f.price || ""} onChange={(e) => setFmt(i, "price", e.target.value)} placeholder="e.g. $9.99" data-testid={`format-price-${f.key}`} />
            </div>
          </div>
        ))}
      </div>
      <p className="font-ui text-xs text-paper/40 mt-4">
        Only formats marked Available with a URL show purchase buttons. Prices are optional — leave blank to send readers to Amazon for current pricing.
      </p>
      <div className="mt-6">
        <SaveBtn onClick={save} saving={saving} tid="book-save-btn" />
      </div>
    </div>
  );
}

const BRAND_GROUPS = [
  { name: "Primary Logo", files: [["Full lockup — black background", "logo_primary.png"], ["Full lockup — transparent", "logo_primary_transparent.png"]] },
  { name: "Monogram (Symbol Only)", files: [["DC symbol — transparent", "logo_symbol.png"], ["DC symbol — flat white", "flat_symbol_white.png"]] },
  { name: "Horizontal", files: [["Horizontal lockup — headers", "logo_horizontal.png"]] },
  { name: "Monochrome", files: [["Full lockup — white", "logo_white.png"], ["Full lockup — black", "logo_black.png"], ["Symbol — white", "logo_symbol_white.png"]] },
  { name: "Trademark Preparation", files: [["Flat white — full lockup", "flat_white_full.png"], ["Flat black — full lockup", "flat_black_full.png"], ["Flat black — symbol", "flat_symbol_black.png"]] },
  { name: "Social Media Branding", files: [["Share card 1200×630", "og_image.png"]] },
  { name: "Merchandise Artwork", files: [["Tee / Hoodie placement", "merch_tee.png"], ["Embroidered hat", "merch_hat.png"], ["Journal cover", "merch_journal.png"], ["Mug", "merch_mug.png"], ["Tote bag", "merch_tote.png"], ["Bookmark", "merch_bookmark.png"], ["Premium packaging", "merch_packaging.png"]] },
];

export function BrandTab() {
  return (
    <div data-testid="admin-tab-brand">
      <h2 className="font-display text-2xl text-paper mb-3">Brand Asset Library</h2>
      <p className="font-ui text-sm text-paper/50 mb-10 max-w-2xl leading-relaxed">
        Official Dirty Church Chronicles identity — one master design, approved variations only. High-resolution
        masters, clear-space rules, and trademark-preparation notes live in the project's brand-assets folder
        (README.md). Never regenerate or restyle the logo per page.
      </p>
      {BRAND_GROUPS.map((g) => (
        <div key={g.name} className="mb-10">
          <p className="eyebrow mb-4">{g.name}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {g.files.map(([label, file]) => (
              <div key={file} className="border border-paper/10 bg-coal" data-testid={`brand-${file}`}>
                <div className="aspect-square flex items-center justify-center p-6 bg-[#0a0a0a]">
                  <img src={assetUrl(`/api/uploads/${file}`)} alt={label} className="max-w-full max-h-full object-contain" loading="lazy" />
                </div>
                <div className="p-3 border-t border-paper/10 flex items-center justify-between gap-2">
                  <p className="font-ui text-xs text-paper/60">{label}</p>
                  <a href={assetUrl(`/api/uploads/${file}`)} target="_blank" rel="noopener noreferrer"
                    data-testid={`brand-dl-${file}`}
                    className="font-ui text-[10px] uppercase tracking-[0.15em] text-crimsonhi hover:text-paper transition-colors shrink-0">
                    Open
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="font-ui text-xs text-paper/35 mt-4 max-w-2xl leading-relaxed">
        Note: the master is a textured high-resolution painting. True SVG vectors require a professional manual
        redraw (auto-tracing would degrade the emblem) — flat single-color versions above are ready for
        embroidery and single-color print. No trademark registration is claimed; do not use ®.
      </p>
    </div>
  );
}

export function AudienceTab() {
  const [subs, setSubs] = useState(null);
  const [inqs, setInqs] = useState(null);
  const [interests, setInterests] = useState(null);
  const load = () => {
    api.get("/admin/audience/subscribers").then((r) => setSubs(r.data)).catch(() => setSubs([]));
    api.get("/admin/audience/inquiries").then((r) => setInqs(r.data)).catch(() => setInqs([]));
    api.get("/admin/audience/interests").then((r) => setInterests(r.data)).catch(() => setInterests([]));
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = async (path, filename) => {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const delSub = async (email) => {
    if (!window.confirm(`Remove ${email} from the reader list?`)) return;
    await api.delete(`/admin/audience/subscribers/${encodeURIComponent(email)}`);
    load();
  };
  const delInq = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/audience/inquiries/${id}`);
    load();
  };
  const markRead = async (id) => {
    await api.put(`/admin/audience/inquiries/${id}`, { read: true });
    load();
  };

  return (
    <div data-testid="admin-tab-audience">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-display text-2xl text-paper">Reader List</h2>
        <button onClick={() => downloadCsv("/admin/audience/subscribers.csv", "reader-list.csv")} data-testid="export-subscribers-btn"
          className="inline-flex items-center gap-2 font-ui text-[11px] uppercase tracking-[0.2em] border border-paper/25 text-paper/80 px-5 py-2.5 rounded-full hover:border-paper/60 transition-colors duration-300">
          <Download size={13} /> Export CSV
        </button>
      </div>
      {subs === null ? <p className="font-ui text-sm text-paper/40">Loading…</p> : subs.length === 0 ? (
        <p className="font-ui text-sm text-paper/40 border border-paper/10 p-8 mb-12">No subscribers yet.</p>
      ) : (
        <div className="divide-y divide-paper/10 border-y border-paper/10 mb-12" data-testid="subscribers-table">
          {subs.map((s) => (
            <div key={s.email} className="py-3.5 flex items-center justify-between gap-4">
              <div>
                <p className="font-ui text-sm text-paper">{s.email}</p>
                <p className="font-ui text-xs text-paper/40 mt-0.5">Consent recorded {new Date(s.consent_at).toLocaleString()}</p>
              </div>
              <button onClick={() => delSub(s.email)} aria-label={`Delete ${s.email}`} data-testid={`sub-delete-${s.email}`}
                className="p-2 text-paper/50 hover:text-crimsonhi transition-colors"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-display text-2xl text-paper">Inquiries</h2>
        <button onClick={() => downloadCsv("/admin/audience/inquiries.csv", "inquiries.csv")} data-testid="export-inquiries-btn"
          className="inline-flex items-center gap-2 font-ui text-[11px] uppercase tracking-[0.2em] border border-paper/25 text-paper/80 px-5 py-2.5 rounded-full hover:border-paper/60 transition-colors duration-300">
          <Download size={13} /> Export CSV
        </button>
      </div>
      {inqs === null ? <p className="font-ui text-sm text-paper/40">Loading…</p> : inqs.length === 0 ? (
        <p className="font-ui text-sm text-paper/40 border border-paper/10 p-8">No inquiries yet.</p>
      ) : (
        <div className="divide-y divide-paper/10 border-y border-paper/10" data-testid="inquiries-table">
          {inqs.map((q) => (
            <div key={q.inquiry_id} className={`py-5 ${q.read ? "opacity-55" : ""}`} data-testid={`inquiry-${q.inquiry_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-ui text-sm text-paper font-semibold">{q.name} <span className="text-paper/40 font-normal">&lt;{q.email}&gt;</span></p>
                  <p className="font-ui text-[10px] uppercase tracking-[0.2em] text-crimsonhi mt-1">{q.category} · {new Date(q.created_at).toLocaleString()}</p>
                  <p className="font-ui text-sm text-paper/60 mt-3 whitespace-pre-wrap max-w-2xl">{q.message}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {!q.read && (
                    <button onClick={() => markRead(q.inquiry_id)} aria-label="Mark read" data-testid={`inquiry-read-${q.inquiry_id}`}
                      className="p-2 text-paper/50 hover:text-gold transition-colors"><Eye size={15} /></button>
                  )}
                  <button onClick={() => delInq(q.inquiry_id)} aria-label="Delete inquiry" data-testid={`inquiry-delete-${q.inquiry_id}`}
                    className="p-2 text-paper/50 hover:text-crimsonhi transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-2xl text-paper mt-14 mb-6">Product Launch Interest</h2>
      {interests === null ? <p className="font-ui text-sm text-paper/40">Loading…</p> : interests.length === 0 ? (
        <p className="font-ui text-sm text-paper/40 border border-paper/10 p-8">No product notifications requested yet.</p>
      ) : (
        <div className="divide-y divide-paper/10 border-y border-paper/10" data-testid="interests-table">
          {interests.map((x, i) => (
            <div key={i} className="py-3.5 flex items-center justify-between gap-4">
              <p className="font-ui text-sm text-paper">{x.email}</p>
              <p className="font-ui text-xs text-paper/45">{x.product_title} · {new Date(x.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
